import { supabase } from "../lib/supabase";

export type NovaCmsMenuGroup =
  | "food"
  | "drinks";

export type NovaCmsMenuCategory = {
  id: string;
  name: string;
  description: string | null;
  group_name: NovaCmsMenuGroup;
  sort_order: number;
  is_published: boolean;
  created_at?: string;
  updated_at?: string;
};

export type NovaCmsMenuItem = {
  id: string;
  category_id: string;
  name: string;
  description: string | null;
  base_price: number | null;
  image_url: string | null;
  sort_order: number;
  is_available: boolean;
  is_featured: boolean;
  ordering_enabled: boolean;
  website_visible: boolean;
  qr_visible: boolean;
  is_published: boolean;
  created_at?: string;
  updated_at?: string;
};

export type NovaCmsMenuVariant = {
  id: string;
  item_id: string;
  label: string;
  price: number;
  sort_order: number;
  is_available: boolean;
  is_published: boolean;
  created_at?: string;
  updated_at?: string;
};

export type NovaCmsMenuModifier = {
  id: string;
  category_id: string | null;
  item_id: string | null;
  name: string;
  price_delta: number;
  sort_order: number;
  is_available: boolean;
  is_published: boolean;
  created_at?: string;
  updated_at?: string;
};

export type NovaCmsMenuCatalog = {
  categories: NovaCmsMenuCategory[];
  items: NovaCmsMenuItem[];
  variants: NovaCmsMenuVariant[];
  modifiers: NovaCmsMenuModifier[];
};

function errorMessage(
  error: unknown,
  fallback: string,
) {
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }

  return fallback;
}

function rpcError(
  error: unknown,
  fallback: string,
) {
  throw new Error(
    errorMessage(
      error,
      fallback,
    ),
  );
}

export async function loadNovaCmsMenuCatalog(): Promise<NovaCmsMenuCatalog> {
  const {
    data,
    error,
  } = await supabase.rpc(
    "get_nova_cms_menu_catalog",
  );

  if (error) {
    rpcError(
      error,
      "Could not load the Nova menu.",
    );
  }

  const raw =
    (
      data ??
      {}
    ) as Partial<NovaCmsMenuCatalog>;

  return {
    categories:
      Array.isArray(
        raw.categories,
      )
        ? raw.categories
        : [],

    items:
      Array.isArray(
        raw.items,
      )
        ? raw.items
        : [],

    variants:
      Array.isArray(
        raw.variants,
      )
        ? raw.variants
        : [],

    modifiers:
      Array.isArray(
        raw.modifiers,
      )
        ? raw.modifiers
        : [],
  };
}

export async function saveNovaCmsMenuCategory(
  category: NovaCmsMenuCategory,
): Promise<void> {
  const { error } =
    await supabase.rpc(
      "save_nova_cms_menu_category",
      {
        p_category:
          category,
      },
    );

  if (error) {
    rpcError(
      error,
      "Could not save menu category.",
    );
  }
}

export async function saveNovaCmsMenuItem(
  item: NovaCmsMenuItem,
): Promise<void> {
  const { error } =
    await supabase.rpc(
      "save_nova_cms_menu_item",
      {
        p_item:
          item,
      },
    );

  if (error) {
    rpcError(
      error,
      "Could not save menu item.",
    );
  }
}

export async function saveNovaCmsMenuVariant(
  variant: NovaCmsMenuVariant,
): Promise<void> {
  const { error } =
    await supabase.rpc(
      "save_nova_cms_menu_variant",
      {
        p_variant:
          variant,
      },
    );

  if (error) {
    rpcError(
      error,
      "Could not save menu variant.",
    );
  }
}

export async function removeNovaCmsMenuVariant(
  itemId: string,
  variantId: string,
): Promise<void> {
  const { error } =
    await supabase.rpc(
      "remove_nova_cms_menu_variant",
      {
        p_item_id:
          itemId,

        p_variant_id:
          variantId,
      },
    );

  if (error) {
    rpcError(
      error,
      "Could not remove menu variant.",
    );
  }
}

export async function saveNovaCmsMenuModifier(
  modifier: NovaCmsMenuModifier,
): Promise<void> {
  const { error } =
    await supabase.rpc(
      "save_nova_cms_menu_modifier",
      {
        p_modifier:
          modifier,
      },
    );

  if (error) {
    rpcError(
      error,
      "Could not save menu modifier.",
    );
  }
}

export async function removeNovaCmsMenuModifier(
  modifierId: string,
): Promise<void> {
  const { error } =
    await supabase.rpc(
      "remove_nova_cms_menu_modifier",
      {
        p_modifier_id:
          modifierId,
      },
    );

  if (error) {
    rpcError(
      error,
      "Could not remove menu modifier.",
    );
  }
}

export async function setNovaCmsMenuItemArchived(
  itemId: string,
  archived: boolean,
): Promise<void> {
  const { error } =
    await supabase.rpc(
      "set_nova_cms_menu_item_archived",
      {
        p_item_id:
          itemId,

        p_archived:
          archived,
      },
    );

  if (error) {
    rpcError(
      error,
      archived
        ? "Could not archive menu item."
        : "Could not restore menu item.",
    );
  }
}
